import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSearch, FiPlus, FiTrash2 } from 'react-icons/fi';
import { adminApi } from '../adminApi';

function AdminOrderCreate() {
  const navigate = useNavigate();
  const [products, setProducts]     = useState([]);
  const [search, setSearch]         = useState('');
  const [items, setItems]           = useState([]);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchRef = useRef(null);
  const dropRef   = useRef(null);

  const [form, setForm] = useState({
    guest_name: '', phone: '', guest_email: '',
    delivery_address: '', payment_method: 'cod',
    shipping_amount: '', status: 'pending',
  });

  useEffect(() => {
    adminApi.getProducts().then(r => setProducts(r.data)).catch(() => {});
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = e => {
      if (dropRef.current && !dropRef.current.contains(e.target) &&
          searchRef.current && !searchRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = search.trim().length > 0
    ? products.filter(p => p.name.toLowerCase().includes(search.toLowerCase())).slice(0, 8)
    : [];

  const addItem = (product) => {
    setItems(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      if (existing) return prev.map(i => i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, {
        product_id: product.id,
        name: product.name,
        image_url: product.image_url,
        quantity: 1,
        price: product.discount_price || product.price,
      }];
    });
    setSearch('');
    setDropdownOpen(false);
  };

  const updateItem = (productId, field, value) =>
    setItems(prev => prev.map(i => i.product_id === productId ? { ...i, [field]: value } : i));

  const removeItem = (productId) =>
    setItems(prev => prev.filter(i => i.product_id !== productId));

  const subtotal = items.reduce((acc, i) => acc + Number(i.price) * Number(i.quantity), 0);
  const shipping = Number(form.shipping_amount) || 0;
  const total    = subtotal + shipping;

  const setF = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async () => {
    setError('');
    if (!form.guest_name.trim())       { setError('Customer name is required'); return; }
    if (!form.phone.trim())            { setError('Phone number is required'); return; }
    if (!form.delivery_address.trim()) { setError('Delivery address is required'); return; }
    if (items.length === 0)            { setError('Add at least one product'); return; }

    setSaving(true);
    try {
      const res = await adminApi.createOrder({
        ...form,
        shipping_amount: shipping,
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity, price: Number(i.price) })),
      });
      navigate(`/orders/${res.data.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create order');
      setSaving(false);
    }
  };

  return (
    <div className="oc-page">
      {/* Header */}
      <div className="oc-header">
        <button className="oc-back-btn" onClick={() => navigate('/orders')}>
          <FiArrowLeft size={15} /> Orders
        </button>
        <div className="oc-header-title">Create New Order</div>
      </div>

      <div className="oc-body">
        {/* ── Left column ── */}
        <div className="oc-left">
          {/* Customer Info */}
          <div className="oc-card">
            <div className="oc-card-title">Customer Info</div>
            <div className="oc-field">
              <label>Name <span className="oc-req">*</span></label>
              <input value={form.guest_name} onChange={e => setF('guest_name', e.target.value)} placeholder="Full name" />
            </div>
            <div className="oc-field">
              <label>Phone <span className="oc-req">*</span></label>
              <input value={form.phone} onChange={e => setF('phone', e.target.value)} placeholder="+92 300 0000000" />
            </div>
            <div className="oc-field">
              <label>Email</label>
              <input value={form.guest_email} onChange={e => setF('guest_email', e.target.value)} placeholder="Optional" />
            </div>
            <div className="oc-field">
              <label>Delivery Address <span className="oc-req">*</span></label>
              <textarea rows={3} value={form.delivery_address} onChange={e => setF('delivery_address', e.target.value)} placeholder="Street, city, province" />
            </div>
          </div>

          {/* Payment & Options */}
          <div className="oc-card">
            <div className="oc-card-title">Payment & Options</div>
            <div className="oc-field">
              <label>Payment Method</label>
              <div className="oc-pay-toggle">
                {['cod', 'paid'].map(m => (
                  <button key={m} className={`oc-pay-btn ${form.payment_method === m ? 'active' : ''}`}
                    onClick={() => setF('payment_method', m)}>
                    {m === 'cod' ? 'Cash on Delivery' : 'Paid / Prepaid'}
                  </button>
                ))}
              </div>
            </div>
            <div className="oc-field">
              <label>Shipping (Rs)</label>
              <input type="number" min="0" value={form.shipping_amount}
                onChange={e => setF('shipping_amount', e.target.value)} placeholder="0" />
            </div>
            <div className="oc-field">
              <label>Order Status</label>
              <select value={form.status} onChange={e => setF('status', e.target.value)}>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="shipped">Shipped</option>
              </select>
            </div>
          </div>
        </div>

        {/* ── Right column ── */}
        <div className="oc-right">
          <div className="oc-card oc-items-card">
            <div className="oc-card-title">Order Items</div>

            {/* Product search */}
            <div className="oc-search-wrap" ref={dropRef}>
              <div className="oc-search-box" ref={searchRef}>
                <FiSearch size={14} color="#94a3b8" />
                <input
                  placeholder="Search products to add…"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setDropdownOpen(true); }}
                  onFocus={() => search && setDropdownOpen(true)}
                />
              </div>
              {dropdownOpen && filtered.length > 0 && (
                <div className="oc-product-dropdown">
                  {filtered.map(p => (
                    <button key={p.id} className="oc-product-item" onMouseDown={() => addItem(p)}>
                      {p.image_url
                        ? <img src={p.image_url} alt="" className="oc-product-img" onError={e => e.target.style.display='none'} />
                        : <div className="oc-product-img-ph" />
                      }
                      <div className="oc-product-info">
                        <span className="oc-product-name">{p.name}</span>
                        <span className="oc-product-price">Rs {(p.discount_price || p.price).toLocaleString()}</span>
                      </div>
                      <FiPlus size={14} color="#6366f1" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Items list */}
            {items.length === 0 ? (
              <div className="oc-empty">Search above and add products to this order</div>
            ) : (
              <div className="oc-items-list">
                <div className="oc-items-header">
                  <span>Product</span><span>Qty</span><span>Price</span><span>Total</span><span />
                </div>
                {items.map(item => (
                  <div key={item.product_id} className="oc-item-row">
                    <div className="oc-item-name-cell">
                      {item.image_url
                        ? <img src={item.image_url} alt="" className="oc-item-img" onError={e => e.target.style.display='none'} />
                        : <div className="oc-item-img-ph" />
                      }
                      <span className="oc-item-name">{item.name}</span>
                    </div>
                    <input type="number" min="1" value={item.quantity}
                      onChange={e => updateItem(item.product_id, 'quantity', Math.max(1, Number(e.target.value)))}
                      className="oc-qty-input" />
                    <input type="number" min="0" value={item.price}
                      onChange={e => updateItem(item.product_id, 'price', e.target.value)}
                      className="oc-price-input" />
                    <span className="oc-item-total">
                      Rs {(Number(item.price) * Number(item.quantity)).toLocaleString()}
                    </span>
                    <button className="oc-remove-btn" onClick={() => removeItem(item.product_id)}>
                      <FiTrash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Totals */}
            <div className="oc-totals">
              <div className="oc-total-row"><span>Subtotal</span><span>Rs {subtotal.toLocaleString()}</span></div>
              <div className="oc-total-row"><span>Shipping</span><span>Rs {shipping.toLocaleString()}</span></div>
              <div className="oc-total-row oc-total-grand"><span>Total</span><span>Rs {total.toLocaleString()}</span></div>
            </div>
          </div>

          {error && <div className="oc-error">{error}</div>}

          <div className="oc-actions">
            <button className="oc-cancel-btn" onClick={() => navigate('/orders')}>Cancel</button>
            <button className="oc-submit-btn" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Creating…' : 'Create Order'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminOrderCreate;
